import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Grid,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const data = [
  { name: "New Customers", value: 35 },
  { name: "Returning Customers", value: 65 },
];

const COLORS = ["#3E7EFF", "#379E66"];

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: "white",
          p: 1.5,
          borderRadius: 1,
          boxShadow: 2,
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {payload[0].name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {payload[0].value}%
        </Typography>
      </Box>
    );
  }
  return null;
};

export default function CustomerInsights({
  data: propData,
}: {
  data?: {
    newCustomers: number;
    returningCustomers: number;
    newCustomersPercentage: number;
    returningCustomersPercentage: number;
  };
}) {
  const chartData = propData
    ? [
        { name: "New Customers", value: propData.newCustomersPercentage },
        {
          name: "Returning Customers",
          value: propData.returningCustomersPercentage,
        },
      ]
    : data;

  return (
    <Box mt={5}>
      <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              borderBottom: "1px solid #E4E7EC",
              pb: 2,
              mb: 4,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: 18, sm: 20 } }}
              >
                Customer Breakdown
              </Typography>
              <Typography variant="body2">
                Track growth by comparing new and returning customers
              </Typography>
            </Box>
            <IconButton size="small">
              <MoreVert />
            </IconButton>
          </Box>

          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={110}
                startAngle={90}
                endAngle={450}
                paddingAngle={1}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 3, my: 3 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: COLORS[0],
                }}
              />
              <Typography variant="body2">
                {propData?.newCustomers} New Customers
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: COLORS[1],
                }}
              />
              <Typography variant="body2">
                {propData?.returningCustomers} Returning Customers
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  bgcolor: "#3E7EFF",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "center",
                  color: "white",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, mb: 0.5, fontSize: 16 }}
                >
                  {propData?.newCustomersPercentage}%
                </Typography>
                <Typography fontWeight={400} fontSize="12px" variant="body2">
                  New Customers
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  bgcolor: "#379E66",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "center",
                  color: "white",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, mb: 0.5, fontSize: 16 }}
                >
                  {propData?.returningCustomersPercentage}%
                </Typography>
                <Typography fontWeight={400} fontSize="12px" variant="body2">
                  Returning Customers
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
